import { useSuspenseQueries } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    createPalette,
    type UsePaletteTrigger,
    type UsePaletteTriggerProps,
    usePaletteTrigger,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { DialogRoot, type OverlayTriggerState, useDialogTriggerState } from '@gitbook/spine-react';

import { OpenAPISpecCreateDialog } from '../../Components';
import { apiQuery } from '../../state';

interface OpenAPISpecSelectorProps {
    organizationId: string;
    children: (
        props: UsePaletteTrigger & {
            openAPISpecs: api.OpenAPISpec[];
            addOpenAPISpecTrigger: OverlayTriggerState;
        }
    ) => ReactNode;
    onChange: (openAPISpec: api.OpenAPISpec) => void;
    analyticsContext: analytics.OpenAPIAnalyticsCreateContext;
    triggerRef?: UsePaletteTriggerProps['ref'];
}

/**
 * Primitive to build an OpenAPI specification selector.
 */
export function OpenAPISpecSelector(props: OpenAPISpecSelectorProps) {
    const { organizationId, children, onChange, analyticsContext } = props;
    const [{ data }, { data: organization }] = useSuspenseQueries({
        queries: [
            apiQuery('listOpenAPISpecs', { organizationId, limit: 100 }),
            apiQuery('getOrganizationById', { organizationId }),
        ],
    });
    return (
        <OpenAPISpecSwitcher
            organization={organization}
            openAPISpecs={data.items}
            onChange={onChange}
            analyticsContext={analyticsContext}
        >
            {children}
        </OpenAPISpecSwitcher>
    );
}

interface OpenAPISpecSwitcherProps
    extends Pick<OpenAPISpecSelectorProps, 'children' | 'onChange' | 'triggerRef'> {
    organization: api.Organization;
    openAPISpecs: api.OpenAPISpec[];
    analyticsContext: analytics.OpenAPIAnalyticsCreateContext;
}

function OpenAPISpecSwitcher(props: OpenAPISpecSwitcherProps) {
    const { organization, openAPISpecs, children, onChange, triggerRef, analyticsContext } = props;
    const addOpenAPISpecTrigger = useDialogTriggerState({
        onOpenChange: (isOpen) => {
            if (isOpen) {
                analytics.logEvent({
                    type: 'openapi_spec_create_intent',
                    organization: organization.id,
                    trigger: 'menu-action',
                    ...analyticsContext,
                });
            }
        },
    });
    const paletteTrigger = usePaletteTrigger({
        ref: triggerRef,
        palette: () => {
            return createPalette({
                groups: [
                    {
                        key: 'openapi',
                        commands: [
                            ...openAPISpecs.map((spec) => ({
                                key: spec.id,
                                title: spec.slug,
                                trigger: () => {
                                    onChange(spec);
                                },
                            })),
                            organization.permissions.createOpenAPISpec
                                ? {
                                      key: 'add',
                                      title: 'Add specification',
                                      icon: Icon.Plus,
                                      trigger: () => {
                                          addOpenAPISpecTrigger.open();
                                      },
                                  }
                                : null,
                        ].filter((x) => x !== null),
                    },
                ],
            });
        },
    });
    return (
        <>
            <DialogRoot {...addOpenAPISpecTrigger.rootProps}>
                <OpenAPISpecCreateDialog
                    organizationId={organization.id}
                    onCreated={(openApiSpec) => {
                        onChange(openApiSpec);
                    }}
                    analyticsContext={analyticsContext}
                />
            </DialogRoot>
            {children({ ...paletteTrigger, openAPISpecs, addOpenAPISpecTrigger })}
        </>
    );
}
